import { codeNameLabel } from '@/shared/utils/codeNameLabel';
import { env } from '@/app/config/env';
import { isMegaAdminRoleName } from '@/core/security/roles';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { MASTER_FALLBACK_ERROR } from '../constants/masters.constants';
import type { PagedContent } from '../models/baseMaster.types';
import { fetchAssignedHierarchy } from './assignedHierarchyApi';

/**
 * The choice lists a master form offers.
 *
 * A record form's selects are reference data, not the master's own rows, so
 * they are fetched once per page rather than per dialog. Geography arrives
 * with the parent it belongs to, which is what lets State be scoped to Country
 * and City to State without a round trip per keystroke.
 */

/** One option, and the parent that scopes it where there is one. */
export interface MasterOption {
  /** The row's identifier, where the endpoint carries one. */
  id?: number;
  value: string;
  /** The owning country for a state, the owning state for a city. */
  parent?: string;
  /**
   * The row's business code, where it has one — `ORG-001` for an organization.
   *
   * Carried so a caller holding a code can find the option whose label matches it. The signed-in user's
   * organization is known by code (it is what `/users/me` reports and what the session stores), while the
   * form works in labels; without this the two could not be matched up.
   */
  code?: string;
  /**
   * What a dropdown shows for the row: `CODE - NAME` when the row has a code, the name alone when it has none
   * (`codeNameLabel`). Display only -- `value` (and `code`) are still what a control stores and a request
   * sends, so nothing that matches on them changes.
   */
  label?: string;
  /** A Phone Code row's country, as its ISO 3166 code (`IN`) -- what the dial-code select draws its flag from. */
  isoCode?: string;
}

/** Which list to fetch. Mirrors `optionSource` on a field descriptor. */
export type MasterOptionSource =
  | 'country'
  | 'state'
  | 'city'
  | 'currency'
  | 'dateFormat'
  | 'timeFormat'
  | 'timeZone'
  /*
    The Total Employee choices, from the enum that is their single source of truth. They used to be five
    hardcoded labels on the Organization definition -- and not one of the five matched what the enum stores,
    so the control rendered blank for every organization and a save was refused as an unrecognised value.
  */
  | 'employeeCount'
  | 'organization'
  /*
    The two derived from `GET /users/me/assigned-hierarchy` rather than from a listing of their own.

    `organization` and `entity` answer what the *platform* holds; these answer what the *caller* holds, which
    is the question an Add form is actually asking. They are listed here so a definition can name one and the
    existing machinery -- `optionsFor`, `idFor`, `scopedBy` -- keeps working unchanged; what differs is only
    where the rows come from and when they are fetched (on a popup open, not on page load).
  */
  | 'assignedOrganization'
  | 'assignedEntity'
  | 'location'
  /* The hierarchy the Business Unit, Division and Department forms pick a parent from. */
  | 'entity'
  /*
    Every entity the caller holds, whichever one the shell has selected: `/legal-entities` read without
    `X-Entity-Id`. What User Management's Additional Entity Admin Access offers -- the selection narrows what a
    screen SHOWS, not which of their entities an administrator may grant.
  */
  | 'accessibleEntity'
  | 'businessUnit'
  | 'division'
  /*
    The bottom of that chain. Added for the Location form, whose Department control was a hardcoded list of
    five names -- five names that exist in no database, so choosing one wrote a department nothing could
    resolve. Every level of the chain now comes from its own endpoint.
  */
  | 'department'
  | 'status'
  /* The masters built to the design reference, each sourcing another master's list. */
  | 'category'
  | 'uom'
  | 'supplier'
  | 'paymentTerms'
  /* The Delivery Terms master, Supplier onboarding's Preferred Delivery Term (design16). */
  | 'deliveryTerms'
  /*
    The Incoterm master, which the Delivery Terms form picks its rule from. It was free text on that form
    until the master existed, which admitted 'exw', 'Ex-works' and a typo as readily as EXW -- three
    spellings of one rule that nothing could group, filter or report on.
  */
  | 'incoterm'
  | 'designation'
  | 'userRole'
  | 'user'
  /*
    User Management's Reporting Manager (2026-10-10): the Active users of the selected entity, never a Mega Admin
    nor the user being edited, narrowed by the business unit, division or department the form holds. Read per
    combination of those (`fetchWith`), not once per page -- see `useMasterOptions`.
  */
  | 'reportingManager'
  /*
    The dial codes, for the Organization master's Contact Information block.

    Its rows are read through `optionValue: 'code'` rather than by label: the control offers `+91`, which is
    `opcc_code`, while `opcc_name` is the country and is deliberately not shown -- the user is choosing a
    dial code, not a country, and the two are already separate fields on that form.
  */
  | 'phoneCode';

const ENDPOINTS: Record<MasterOptionSource, string> = {
  country: '/countries',
  state: '/states',
  city: '/cities',
  currency: '/currencies',
  dateFormat: '/date-formats',
  timeFormat: '/time-formats',
  timeZone: '/timezones',
  employeeCount: '/organizations/employee-count-ranges',
  organization: '/organizations',
  /* Both derived from one read; `fetch` branches before it ever reaches this table. */
  assignedOrganization: '/users/me/assigned-hierarchy',
  assignedEntity: '/users/me/assigned-hierarchy',
  location: '/locations',
  entity: '/legal-entities',
  accessibleEntity: '/legal-entities',
  businessUnit: '/business-units',
  division: '/divisions',
  department: '/departments',
  status: '/statuses',
  /* Not '/categories': that path is configuration.master's generic lookup table. */
  category: '/product-categories',
  uom: '/uoms',
  supplier: '/suppliers',
  paymentTerms: '/payment-terms',
  deliveryTerms: '/delivery-terms',
  incoterm: '/incoterms',
  designation: '/designations',
  userRole: '/roles',
  user: '/users',
  reportingManager: '/users/reporting-manager-options',
  phoneCode: '/phone-country-codes',
};

/**
 * The reference's own lists, used when mocks are on.
 *
 * They are the design's shape and ordering, not data to ship: with mocks off
 * every one of these comes from the endpoint above.
 */
const MOCKS: Record<MasterOptionSource, MasterOption[]> = {
  country: [
    { value: 'India' },
    { value: 'United States' },
    { value: 'United Arab Emirates' },
    { value: 'Singapore' },
    { value: 'United Kingdom' },
  ],
  state: [
    { value: 'Maharashtra', parent: 'India' },
    { value: 'Karnataka', parent: 'India' },
    { value: 'Gujarat', parent: 'India' },
    { value: 'Tamil Nadu', parent: 'India' },
    { value: 'Delhi', parent: 'India' },
  ],
  city: [
    { value: 'Pune', parent: 'Maharashtra' },
    { value: 'Mumbai', parent: 'Maharashtra' },
    { value: 'Nagpur', parent: 'Maharashtra' },
    { value: 'Bengaluru', parent: 'Karnataka' },
    { value: 'Ahmedabad', parent: 'Gujarat' },
  ],
  currency: [
    { value: 'INR - Indian Rupee' },
    { value: 'USD - US Dollar' },
    { value: 'EUR - Euro' },
    { value: 'AED - UAE Dirham' },
    { value: 'GBP - Pound Sterling' },
  ],
  /* The platform's composed labels (SiteFormatLabels), so mock mode reads as the real dropdowns do. */
  dateFormat: [
    { value: 'DD/MM/YYYY - Day, Month & Four Digit Year' },
    { value: 'MM/DD/YYYY - Month, Day & Four Digit Year' },
    { value: 'YYYY-MM-DD - Four Digit Year, Month & Day' },
    { value: 'DD-MM-YYYY - Day, Month & Four Digit Year' },
    { value: 'MM-DD-YYYY - Month, Day & Four Digit Year' },
    { value: 'DD-MMM-YY - Day, Abbreviated Month & Two Digit Year' },
  ],
  timeFormat: [
    { value: 'HH:mm:ss - 24-hour format' },
    { value: 'hh:mm A - 12-hour format with AM/PM without seconds' },
    { value: 'HH:mm - 24-hour format without seconds' },
    { value: 'hh:mm:ss A - 12-hour format with AM/PM' },
  ],
  timeZone: [
    { value: 'IST (+5:30)' },
    { value: 'GST (+4:00)' },
    { value: 'GMT (+0:00)' },
    { value: 'EST (-5:00)' },
    { value: 'PST (-8:00)' },
  ],
  /* The enum's real labels, so the fixture cannot drift from the contract the way the old hardcoded five did. */
  employeeCount: [
    { value: '0-50' },
    { value: '51-100' },
    { value: '101-500' },
    { value: '501-1000' },
    { value: 'More than 1000' },
  ],
  organization: [{ id: 1, value: 'AIT Global Procurement', code: 'ORG-001' }],
  /*
    Unused: `fetch` answers these two from `assignedHierarchyApi`, which carries its own fixture. Present
    because the map is total over the source union, and a placeholder that silently differed from the real
    answer would be worse than none.
  */
  assignedOrganization: [],
  assignedEntity: [],
  location: [{ value: 'Pune' }, { value: 'Nagpur' }, { value: 'Mumbai' }, { value: 'Bengaluru' }],
  entity: [{ id: 1, value: 'AIT Global Pvt Ltd', parent: 'AIT Global Procurement' }],
  accessibleEntity: [
    { id: 1, value: 'AIT Global Pvt Ltd', parent: 'AIT Global Procurement' },
    { id: 2, value: 'AIT Global Inc', parent: 'AIT Global Procurement' },
  ],
  businessUnit: [
    { id: 1, value: 'Procurement', parent: 'AIT Global Pvt Ltd' },
    { id: 2, value: 'Manufacturing', parent: 'AIT Global Pvt Ltd' },
  ],
  division: [
    { id: 1, value: 'Direct Materials', parent: 'Procurement' },
    { id: 2, value: 'Indirect Materials', parent: 'Procurement' },
  ],
  department: [
    { id: 1, value: 'Sourcing', parent: 'Direct Materials' },
    { id: 2, value: 'Contracts', parent: 'Direct Materials' },
  ],
  status: [
    { id: 11, value: 'Active' },
    { id: 13, value: 'Inactive' },
  ],
  category: [
    { value: 'Raw Material' },
    { value: 'Consumables' },
    { value: 'Capital Goods' },
    { value: 'Services' },
  ],
  uom: [
    { value: 'Nos' },
    { value: 'Kg' },
    { value: 'Litre' },
    { value: 'Metre' },
    { value: 'Box' },
  ],
  supplier: [
    { value: 'Sunrise Industries' },
    { value: 'Global Tech Supplies' },
    { value: 'Metro Traders' },
  ],
  deliveryTerms: [{ value: 'Delivered At Place', code: 'DAP' }, { value: 'Free On Board', code: 'FOB' }],
  paymentTerms: [
    { value: 'Net 30' },
    { value: 'Net 45' },
    { value: 'Net 60' },
    { value: 'Advance' },
    { value: 'On Delivery' },
  ],
  /*
    Four of the eleven rules of Incoterms 2020, as the seed writes them. The control shows `CODE - NAME`
    (built by `codeNameLabel` from these two halves) and holds the name, which is what `idFor` matches on.
  */
  incoterm: [
    { id: 1, value: 'Ex Works', code: 'EXW' },
    { id: 2, value: 'Free Carrier', code: 'FCA' },
    { id: 9, value: 'Free On Board', code: 'FOB' },
    { id: 11, value: 'Cost, Insurance and Freight', code: 'CIF' },
  ],
  designation: [
    { value: 'Procurement Head' },
    { value: 'Sourcing Manager' },
    { value: 'Buyer' },
    { value: 'Contracts Executive' },
    { value: 'Finance Controller' },
  ],
  userRole: [
    { value: 'Mega Admin' },
    { value: 'Entity Admin' },
    { value: 'Requestor' },
    { value: 'Approver' },
    { value: 'Viewer' },
  ],
  user: [{ value: 'Ashok Kumar' }, { value: 'Riya Mehta' }, { value: 'Rahul Sharma' }],
  reportingManager: [
    { id: 1, value: 'Ashok Kumar', code: 'EMP-001' },
    { id: 2, value: 'Riya Mehta', code: 'EMP-002' },
    { id: 3, value: 'Rahul Sharma', code: 'EMP-003' },
  ],
  /* Read through `code`, which is what the control shows; the country is the label and stays off screen. */
  phoneCode: [
    { id: 7, value: 'India', code: '+91', isoCode: 'IN' },
    { id: 12, value: 'United States', code: '+1', isoCode: 'US' },
    { id: 11, value: 'United Kingdom', code: '+44', isoCode: 'GB' },
    { id: 8, value: 'United Arab Emirates', code: '+971', isoCode: 'AE' },
    { id: 10, value: 'Singapore', code: '+65', isoCode: 'SG' },
    { id: 9, value: 'Germany', code: '+49', isoCode: 'DE' },
  ],
};

/**
 * What a reference row can call its label and its parent.
 *
 * Wider than it looks because these twenty endpoints do not agree on a name for the
 * label: a location calls it `locationName`, a currency splits it across
 * `currencyCode` and `currencyName`, a user has no single label at all. Each is
 * listed because the row genuinely carries that field, not defensively -- a source
 * whose field is missing here produces an empty option list and a select the user
 * cannot use, which is how Location and Currency were silently empty before.
 */
interface ReferenceRow {
  id?: number | null;
  name?: string | null;
  code?: string | null;
  isoCode?: string | null;
  entityName?: string | null;
  locationName?: string | null;
  /*
    The business codes of the two levels a form may have to write back *as* a code.

    `/legal-entities` answers `entityCode` and `/locations` answers `locationCode` — neither is called
    `code`, so neither reached `MasterOption.code` and both selects offered a name for a field the server
    resolves by code. That is what made every User Management save fail with `Unknown entity 'Entity_01'`:
    the name was sent where the code was expected.
  */
  entityCode?: string | null;
  locationCode?: string | null;
  /*
    The other levels' and masters' own codes, which the endpoints answer under their own names. Read for the
    dropdown's `CODE - NAME` label only -- not into `code`, so what a field stores is unchanged.
  */
  businessUnitCode?: string | null;
  divisionCode?: string | null;
  departmentCode?: string | null;
  designationCode?: string | null;
  employeeCode?: string | null;
  currencyCode?: string | null;
  currencyName?: string | null;
  /*
    The three site-configuration masters name their label after themselves: `/date-formats` answers
    `dateFormat`, `/time-formats` `timeFormat`, `/timezones` `timeZone`. None of them was listed here, so
    `toOption` found no label on any row and returned null for every one — all three selects were rendering
    with **zero options**, which is why nothing could be pre-selected in them however good the record was.
  */
  dateFormat?: string | null;
  timeFormat?: string | null;
  timeZone?: string | null;
  /* A row whose label is all it has -- the employee-count ranges answer {code, label} and nothing else. */
  label?: string | null;
  /* A user's label is assembled; `userName` is the account name and the fallback. */
  firstName?: string | null;
  lastName?: string | null;
  userName?: string | null;
  /* `/users/reporting-manager-options` answers the person already assembled: `{ id, employeeCode, fullName }`. */
  fullName?: string | null;
  countryName?: string | null;
  stateName?: string | null;
  /*
    An organization names its own label and code after itself — `/organizations` answers
    `organizationName` and `organizationCode`, not `name` and `code`. Neither was listed here, so every
    organization row produced no label and that select rendered with **zero options**, exactly as the three
    site-configuration lists did.
  */
  organizationCode?: string | null;
  /* The hierarchy levels name their parent after the level above them; an entity's parent is its owner. */
  organizationName?: string | null;
  legalEntityName?: string | null;
  businessUnitName?: string | null;
  divisionName?: string | null;
}

/** `INR - Indian Rupee`, which is how the reference writes a currency everywhere it appears. */
function currencyLabel(row: ReferenceRow): string | null {
  if (row.label && row.label.includes(' - ')) return row.label;
  if (row.currencyCode && row.currencyName) {
    if (row.currencyName.startsWith(`${row.currencyCode} - `)) return row.currencyName;
    return `${row.currencyCode} - ${row.currencyName}`;
  }
  if (row.currencyCode && row.name) {
    if (row.name.startsWith(`${row.currencyCode} - `)) return row.name;
    return `${row.currencyCode} - ${row.name}`;
  }
  if (row.currencyCode) return row.currencyCode;
  return row.currencyName ?? null;
}

function locationLabel(row: ReferenceRow): string | null {
  return row.locationName ?? null;
}

function entityLabel(row: ReferenceRow): string | null {
  if (row.locationName) return null;
  return row.entityName ?? (row.locationName ? null : row.name) ?? null;
}

/**
 * A date format, time format or time zone: the platform's composed `label` —
 * `DD-MM-YYYY - Day, Month & Four Digit Year`, `HH:mm:ss - 24-hour format`, `IST (+5:30)` — which is also
 * what an Organization or Entity record carries for its choice, so the two match by construction. The bare
 * description is the fallback for a backend that does not send one.
 */
function siteFormatLabel(row: ReferenceRow): string | null {
  const own = row.dateFormat ?? row.timeFormat ?? row.timeZone ?? null;
  if (own === null) return null;
  const composed = row.label?.trim() ?? '';
  return composed === '' ? own : composed;
}

function personLabel(row: ReferenceRow): string | null {
  if (row.fullName && row.fullName.trim() !== '') return row.fullName.trim();
  const full = [row.firstName, row.lastName].filter(Boolean).join(' ').trim();
  return full === '' ? (row.userName ?? null) : full;
}

function toOption(row: ReferenceRow): MasterOption | null {
  /*
    Order matters, and it is read as "the row's own name first". A `/locations` row carries `locationName`
    *and* `entityName` -- the entity it sits under -- so with `entityName` ahead of `locationName` every
    Location dropdown on the platform rendered the owning entity's name instead of the location's, and two
    locations under one entity were two identical options.

    `locationName` first is safe for an entity row too: those carry no `locationName`, so they still fall
    through to `entityName`. Each level's own label precedes every label it inherits from above it.
  */
  const value =
    currencyLabel(row) ??
    locationLabel(row) ??
    entityLabel(row) ??
    row.name ??
    row.organizationName ??
    siteFormatLabel(row) ??
    personLabel(row) ??
    row.label ??
    row.code ??
    null;
  if (value === null || value === '') return null;

  /*
    `organizationName` sits below the hierarchy's own labels on purpose. An entity row carries it as its
    *parent*, and its own label is `entityName`; only an organization row has nothing above it, so only
    there does it fall through to being the label.
  */

  /*
    The first parent field the row carries, read from the bottom of the hierarchy upwards so that each level
    is scoped by the one directly above it: a department by its division, a division by its business unit, a
    business unit by its entity, an entity by its organization. The geography fields come last so a business
    unit is scoped by its entity rather than by a country field it does not have.
  */
  const parent =
    row.divisionName ??
    row.businessUnitName ??
    row.legalEntityName ??
    row.organizationName ??
    row.stateName ??
    row.countryName ??
    undefined;

  /*
    The id travels with the label because the hierarchy endpoints take a numeric
    parent -- legalEntityId, businessUnitId, divisionId -- while the form works in
    labels. Without it a page would have to guess the id back from the name.
  */
  const id = typeof row.id === 'number' ? row.id : undefined;

  /*
    The business code, for a caller that holds one and needs the label beside it — and for a field whose
    stored value *is* the code (`optionValue: 'code'`).

    Read own-code-first, the same way the label above is: a location row carries its own `locationCode`, so
    taking that before anything it inherits keeps a location's code from being its entity's.
  */
  const code = row.locationCode ?? row.entityCode ?? row.organizationCode ?? row.code ?? undefined;

  /*
    The code a dropdown shows beside the name, read own-code-first like `code`, and covering the levels and
    masters whose code is not called `code`. A row that brings its own composed `label` -- a site format, an
    employee-count range (an enum, not a master) -- keeps it; the currency's value already leads with its
    code, which `codeNameLabel` does not repeat.
  */
  const shownCode =
    row.locationCode ??
    row.entityCode ??
    row.organizationCode ??
    row.businessUnitCode ??
    row.divisionCode ??
    row.departmentCode ??
    row.designationCode ??
    row.employeeCode ??
    (siteFormatLabel(row) === null && (row.label ?? '') === '' ? row.code : null) ??
    row.currencyCode ??
    undefined;

  return {
    value,
    label: codeNameLabel(shownCode, value),
    ...(parent ? { parent } : {}),
    ...(id === undefined ? {} : { id }),
    ...(code ? { code } : {}),
    ...(row.isoCode ? { isoCode: row.isoCode } : {}),
  };
}

/**
 * The caller's own assignment, as options.
 *
 * One read answers both sources: the organizations become one list and their entities, flattened, become the
 * other. Each entity carries its organization as its parent, so a form that scopes Entity by Organization
 * narrows correctly for an administrator assigned across more than one.
 */
async function assignedOptions(source: 'assignedOrganization' | 'assignedEntity'): Promise<MasterOption[]> {
  const { organizations } = await fetchAssignedHierarchy();

  if (source === 'assignedOrganization') {
    return organizations.map((organization) => ({
      id: organization.id,
      value: organization.name,
      label: codeNameLabel(organization.code, organization.name),
      ...(organization.code === null ? {} : { code: organization.code }),
    }));
  }

  return organizations.flatMap((organization) =>
    organization.entities.map((entity) => ({
      id: entity.id,
      value: entity.name,
      label: codeNameLabel(entity.code, entity.name),
      parent: organization.name,
      ...(entity.code === null ? {} : { code: entity.code }),
    })),
  );
}

/**
 * The three sources whose endpoint has no status to filter on.
 *
 * - `status` is the Status Master itself. Asking it for its Active rows is asking which statuses are
 *   themselves active, which is not a question that table answers.
 * - `employeeCount` is a Java enum served as a list, not a master at all.
 * - `userRole` reads `os_role`, which carries no status column — a role has a rank and a maximum count, and
 *   no lifecycle to be in.
 *
 * Every other source's listing takes `status`, so every other dropdown asks for Active rows and gets them
 * from the server. `assignedOrganization` / `assignedEntity` are absent because `fetch` branches away before
 * reaching here.
 */
const SOURCES_WITHOUT_STATUS: ReadonlySet<MasterOptionSource> = new Set([
  'status',
  'employeeCount',
  'userRole',
  /* `os_phone_country_code` carries no status column, so there is no Active to ask for. */
  'phoneCode',
  /* Active users only, by the endpoint's own rule. */
  'reportingManager',
]);

/**
 * Mega Admin is not a role a form may hand out.
 *
 * <p>It is the platform superuser -- {@code os_role.orl_maximum_count} is 1 for it and 10 to 1000 for
 * every other role -- and it is provisioned with the tenant, not assigned from the User Management form.
 * Offering it there invited a second one, which the platform would then have to refuse, and made the most
 * powerful role on the platform a click away in an ordinary dropdown.
 *
 * <p>Filtered here rather than asked of the server, which is the opposite of what this file does for
 * status, and the difference is real: status is a column {@code /roles} can filter on, while "may be
 * assigned" is not a concept {@code os_role} has. The 200-row cap that makes client-side filtering wrong
 * for the masters does not bite either -- there are ten roles.
 *
 * <p><strong>Not a security boundary.</strong> A caller who posts the role name directly is not stopped by
 * this; it stops the form from suggesting it.
 */
function assignable(source: MasterOptionSource, options: MasterOption[]): MasterOption[] {
  if (source !== 'userRole') return options;
  return options.filter((option) => !isMegaAdminRoleName(option.value));
}

/**
 * Every option leaves with a display `label`, whichever path built it -- the mock lists are written by hand
 * and carry none. A code and a name make `CODE - NAME`; a name alone is itself (`codeNameLabel`). The status
 * list and the employee-count ranges are not master records and show their value as it is.
 */
function withLabels(source: MasterOptionSource, options: MasterOption[]): MasterOption[] {
  const plain = source === 'status' || source === 'employeeCount';
  return options.map((option) =>
    option.label !== undefined && !plain
      ? option
      : { ...option, label: plain ? option.value : codeNameLabel(option.code, option.value) },
  );
}

export const masterReferenceApi = {
  /**
   * One source's list. `params` narrows a source whose rows depend on what the form holds (Reporting Manager's
   * business unit, division, department and the user being edited); the others take none.
   */
  async fetch(
    source: MasterOptionSource,
    params: Readonly<Record<string, number>> = {},
  ): Promise<MasterOption[]> {
    /* Checked before the mock branch: this read carries its own fixture, keyed to the same assignment. */
    if (source === 'assignedOrganization' || source === 'assignedEntity') {
      return assignedOptions(source);
    }

    if (env.ENABLE_MOCKS) {
      const excluded = params.excludeUserId;
      return withLabels(
        source,
        assignable(source, MOCKS[source]).filter((option) => excluded === undefined || option.id !== excluded),
      );
    }

    const response = await apiClient.get<
      ApiEnvelope<PagedContent<ReferenceRow> | ReferenceRow[]>
    >(ENDPOINTS[source], {
      /* A refused list is the form's news, not grounds to end the session. */
      _allowForbidden: true,
      /* Every entity the caller holds, not only the one selected in the shell. */
      ...(source === 'accessibleEntity' ? { _allEntities: true } : {}),
      params: {
        page: 0,
        size: 200,
        /*
          A dropdown offers what can still be chosen, which is the Active rows and nothing else.

          Asked of the server, not applied to the answer here. These are the same listing endpoints the
          master tables call, and a table must go on showing Inactive rows — so the difference between the
          two callers is this parameter, not a second endpoint and certainly not a filter over the response.
          Filtering here would also be wrong twice over: the page is capped at 200 rows, so dropping rows
          after the fact silently loses options past the cap, and a rule the client applies is a rule any
          other client can decline to apply.

          The status name, never an os_status_master id: the ids are not part of any published contract and
          a screen holding one would break the moment a deployment renumbered them.
        */
        ...(SOURCES_WITHOUT_STATUS.has(source) ? {} : { status: 'Active' }),
        ...params,
      },
    });

    const data = unwrapEnvelope(response, MASTER_FALLBACK_ERROR);
    const rows = Array.isArray(data) ? data : (data.content ?? []);

    return withLabels(
      source,
      assignable(
        source,
        rows.map(toOption).filter((option): option is MasterOption => option !== null),
      ),
    );
  },
};
